import React, { FC, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Skeleton } from '@material-ui/lab'
import { useAction } from 'out-game/hooks'
import { useRequestApproveDeal } from 'out-game/hooks/useRequest'
import { Alert, Typography } from 'out-game/components'
import { Routes } from 'out-game/constants'
import * as actions from 'out-game/actions/alerts'
import * as actionsChat from 'out-game/actions/ui/chat'
import { resetActiveChatState } from 'out-game/actions/ui/chat'
import * as actionsMerchPage from 'out-game/actions/ui/merchPage'
import * as CtxChat from 'out-game/root/User/Chat/context'
import { Status } from '../../components/Alert'
import ButtonRestyled from '../../components/ButtonRestyled'
import { useTranslationStrict } from 'out-game/hooks'

export const MerchOutOfStock: FC = () => {
  const { t } = useTranslationStrict('personal')

  const open = useSelector((state) => state.alerts.merchOutOfStock)
  const setOpen = useAction(actions.merchOutOfStock)
  const setMerchEditorOpen = useAction(actionsMerchPage.setMerchEditorOpen)
  const setAction = useAction(actionsMerchPage.setAction)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.error}>
      <Typography
        size={22}
        lineHeight={1}
        color="Emphasized"
        className="alert-restyled__text"
      >
          {t('inventory.errors.merchOutOfStock')}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant="green"
        onClick={() => {
          setAction(null)
          setMerchEditorOpen(true)
          setOpen(false)
        }}
      >
        {t('common:edit')}
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertCancelDispute: FC = () => {
  const { t } = useTranslationStrict('personal', { keyPrefix: 'dealChat' })

  const [requestApprove, pending] = useRequestApproveDeal()
  const open = useSelector((state) => state.alerts.cancelDispute)
  const dealId = useSelector((state) => state.ui.chat.dealId)
  const setOpen = useAction(actions.cancelDispute)
  const setDealUpdating = useAction(actionsChat.setDealUpdating)
  const updateMessages = useContext(CtxChat.UpdateMessages)
  const resetActiveChatStateAction = useAction(resetActiveChatState)

  const handleCancelDispute = async () => {
    if (dealId === null) {
      throw new TypeError()
    }
    setDealUpdating(true)
    await requestApprove({
      frozen_id: dealId,
      approve: true,
    })
    updateMessages()
    setOpen(false)
    setDealUpdating(false)
    resetActiveChatStateAction()
  }

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.warning}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        {t('cancelDisputeWarning')}
      </Typography>
      <div
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridColumnGap: 15,
          gridTemplateRows: 50
        }}
      >
        <ButtonRestyled
          style={{
            width: 'auto',
            marginBottom: 0
          }}
          className="og-submit"
          variant='red'
          onClick={() => setOpen(false)}
        >
          {t('no')}
        </ButtonRestyled>
        {pending ? (
          <Skeleton variant="rect" height={50} />
        ) : (
          <ButtonRestyled
            style={{
              width: 'auto',
            }}
            className="og-submit"
            variant='green'
            onClick={handleCancelDispute}
          >
            {t('yes')}
          </ButtonRestyled>
        )}
      </div>
    </Alert>
  )
}

export const AlertOnlyOneDealPerUser: FC = () => {
  const history = useHistory()
  const open = useSelector((state) => state.alerts.onlyOneDealPerUser)
  const setOpen = useAction(actions.onlyOneDealPerUser)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.error}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        Вы не можете оформить новую сделку с данным Пользователем пока не
        завершите текущую сделку с ним
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          setOpen(false)
          history.push(Routes.UserChat)
        }}
      >
        Перейти в чат сделки
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertPublishSuccess: FC = () => {
  const { t } = useTranslationStrict('personal', { keyPrefix: 'inventory' })

  const history = useHistory()
  const open = useSelector((state) => state.alerts.publishSuccess)
  const setOpen = useAction(actions.publishSuccess)
  const setAction = useAction(actionsMerchPage.setAction)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.success}>
      <Typography
        size={22}
        lineHeight={1}
        color="Emphasized"
        className="alert-restyled__text"
      >
          {t('putOnSaleSuccessfully')}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant="green"
        onClick={() => setOpen(false)}
      >
          {t('stayHere')}
      </ButtonRestyled>
      <ButtonRestyled
        className="og-submit"
        variant="green"
        onClick={() => {
          setOpen(false)
          setAction(null)
          history.push(Routes.UserInventory)
        }}
      >
          {t('returnToInventory')}
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertInsufficientBalance: FC = () => {
  const { t } = useTranslationStrict('rates')

  const history = useHistory()
  const open = useSelector((state) => state.alerts.insufficientBalance)
  const setOpen = useAction(actions.insufficientBalance)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.error}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
          {t('notEnoughMoney')}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          setOpen(false)
          history.push(Routes.UserWallet)
        }}
      >
          {t('topUpWallet')}
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertPublishingWithoutSubscription: FC = () => {
  const { t } = useTranslationStrict('rates')

  const history = useHistory()
  const open = useSelector(
    (state) => state.alerts.publishingWithoutSubscription
  )
  const setOpen = useAction(actions.publishingWithoutSubscription)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.error}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
          {t('pleaseActivateTariff')}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          history.push(Routes.Subscription)
          setOpen(false)
        }}
      >
          {t('selectTariff')}
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertChatWithoutSubscription: FC = () => {
  const { t } = useTranslationStrict('personal', { keyPrefix: 'dealChat' })

  const history = useHistory()
  const open = useSelector((state) => state.alerts.chatWithoutSubscription)
  const setOpen = useAction(actions.chatWithoutSubscription)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.error}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        {t('chatRequiresActiveTariff')}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          history.push(Routes.SubscriptionBeta)
          setOpen(false)
        }}
      >
        {t('buyTariff')}
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertBalancePaymentSuccess: FC = () => {
  const history = useHistory()
  const open = useSelector((state) => state.alerts.balancePaymentSuccess)
  const setOpen = useAction(actions.balancePaymentSuccess)

  return (
    <Alert 
      open={open}
      onClose={() => {
        setOpen(false)
        history.push(Routes.UserWallet)
      }}
      status={Status.success}
    >
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        Операция прошла успешно.
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          history.push(Routes.Root)
          setOpen(false)
        }}
      >
        Вернуться на главную
      </ButtonRestyled>
    </Alert>
  )
}

export const AlertBalanceWithdrawalSuccess: FC = () => {
  const history = useHistory()
  const open = useSelector((state) => state.alerts.balanceWithdrawalSuccess)
  const setOpen = useAction(actions.balanceWithdrawalSuccess)

  return (
    <Alert open={open} onClose={() => setOpen(false)} status={Status.success}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        Вывод средств из бумажника прошел успешно.
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={() => {
          history.push(Routes.Root)
          setOpen(false)
        }}
      >
        Вернуться на главную
      </ButtonRestyled>
    </Alert>
  )
}

export const CommonAlert: FC = () => {
  const { t } = useTranslationStrict()

  const alertData = useSelector((state) => state.alerts.commonAlert)
  const { isAlertOpened, text, status } = alertData
  const setAlertData = useAction(actions.commonAlert)

  const closeHandler = () => {
    setAlertData({ isAlertOpened: false, text: '', status: Status.error })
  }

  return (
    <Alert open={isAlertOpened} onClose={closeHandler} status={typeof status !== 'number' ?  Status.error : status}>
      <Typography size={22} lineHeight={1} color="Emphasized" className='alert-restyled__text'>
        {text}
      </Typography>
      <ButtonRestyled
        className="og-submit"
        variant='green'
        onClick={closeHandler}
      >
          {t('close')}
      </ButtonRestyled>
    </Alert>
  )
}
